import {
    ActionIcon,
    Box,
    Button,
    Group,
    Menu,
    UnstyledButton,
    rem,
} from '@mantine/core';

import { IconDots } from '@tabler/icons-react';
import { NavLink } from 'react-router-dom';
import useCampaignAction from '../../../../../http/mutations/campaigns/useCampaignAction';
import { useMemo } from 'react';

const MenuButtonItem = ({ text, onClick, disabled }) => {
    return (
        <Menu.Item
            component={UnstyledButton}
            onClick={onClick}
            disabled={disabled}>
            {text}
        </Menu.Item>
    );
};

const MenuLinkItem = ({ text, path }) => {
    return (
        <Menu.Item
            component={NavLink}
            to={path}>
            {text}
        </Menu.Item>
    );
};

const ActionCell = ({
    cell,
    openDetailsModal,
    openJsonModal,
    openStartModal,
    openReplaceShortLinkModal,
    openTimelineModal,
    setJsonModalProps,
    setCampaign,
    setTab,
}) => {
    const { column, row } = cell;
    const rowData = row.original;

    const { isPending, mutate: campaignAction } = useCampaignAction();

    const StartCampaignButton = ({ ...buttonProps }) => {
        return (
            <Button
                size={'compact-xs'}
                color={'blue'}
                onClick={() => {
                    setCampaign(rowData);
                    openStartModal();
                }}
                {...buttonProps}>
                Start
            </Button>
        );
    };

    const PauseCampaignButton = ({ ...buttonProps }) => {
        const pauseAction = () => {
            campaignAction({
                id: rowData.id,
                model: {
                    action: 'Pause',
                },
            });
        };
        return (
            <Button
                size={'compact-xs'}
                color={'orange.6'}
                {...buttonProps}
                onClick={pauseAction}>
                Pause
            </Button>
        );
    };

    const ResumeCampaignButton = ({ ...buttonProps }) => {
        return (
            <Button
                size={'compact-xs'}
                color={'teal.6'}
                {...buttonProps}
                onClick={() => {
                    setCampaign(rowData);
                    openStartModal();
                }}>
                Resume
            </Button>
        );
    };

    const StopCampaignButton = ({ ...buttonProps }) => {
        const stopAction = () => {
            campaignAction({
                id: rowData.id,
                model: {
                    action: 'Stop',
                },
            });
        };
        return (
            <Button
                size={'compact-xs'}
                color={'red'}
                {...buttonProps}
                onClick={stopAction}>
                Stop
            </Button>
        );
    };

    const dropdownOptions = [
        {
            text: 'Events',
            onClick: () => {
                setCampaign(rowData);
                setTab('events');
                openDetailsModal();
            },
        },
        {
            text: 'Queue Stats',
            onClick: () => {
                setCampaign(rowData);
                setJsonModalProps({
                    header: `${rowData.name} Queue Stats`,
                    jsonObj: rowData.queue,
                });
                openJsonModal();
            },
        },
        {
            text: 'Send Timeline',
            onClick: () => {
                setCampaign(rowData);
                openTimelineModal();
            },
        },
        {
            text: 'Replace ShortLink',
            onClick: () => {
                setCampaign(rowData);
                openReplaceShortLinkModal();
            },
            disabled: rowData.shortLink?.value == null,
        },
    ].filter((x) => x != null);

    const ACTION_BUTTONS = useMemo(() => {
        const statusLookup = {
            None: [StartCampaignButton],
            Queued: [PauseCampaignButton, StopCampaignButton],
            Scheduled: [PauseCampaignButton, StopCampaignButton],
            Provisioning: [PauseCampaignButton, StopCampaignButton],
            Initializing: [PauseCampaignButton, StopCampaignButton],
            Running: [PauseCampaignButton, StopCampaignButton],
            Paused: [ResumeCampaignButton, StopCampaignButton],
            Complete: [],
            Stopped: [],
        };
        return statusLookup[rowData.status];
    }, [rowData.status]);

    return (
        <Group
            miw={100}
            gap={'xs'}
            align={'center'}
            justify={'space-between'}
            preventGrowOverflow={true}
            wrap={false}>
            <Group gap={'xs'}>
                {ACTION_BUTTONS?.map((CampaignActionButton, i) => (
                    <CampaignActionButton
                        key={i}
                        disabled={isPending}
                    />
                ))}
            </Group>
            <Menu disabled={false}>
                <Menu.Target>
                    <ActionIcon
                        color={'dimmed'}
                        variant='transparent'
                        disabled={false}>
                        <IconDots stroke={1.5} />
                    </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                    {dropdownOptions.map((x, i) =>
                        x.path ? (
                            <MenuLinkItem
                                key={i}
                                {...x}
                            />
                        ) : (
                            <MenuButtonItem
                                key={i}
                                {...x}
                            />
                        )
                    )}
                </Menu.Dropdown>
            </Menu>
        </Group>
    );
};

const ActionColumn = ({ ...props }) => ({
    accessorKey: 'actions',
    header: 'Actions',
    size: 1,
    minSize: 160,
    Cell: ({ cell }) => (
        <ActionCell
            cell={cell}
            {...props}
        />
    ),
});
export default ActionColumn;
